<template>
  <div class="transporteurs">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-truck"></i>
        Transporteurs
      </h1>
      <button class="btn btn-primary" @click="showAddModal = true">
        <i class="fas fa-plus"></i>
        Nouveau transporteur
      </button>
    </div>

    <!-- Statistiques -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-truck" style="color: #667eea;"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ transporteurs.length }}</div>
          <div class="stat-label">Transporteurs actifs</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-shipping-fast" style="color: #48bb78;"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ livraisons.enCours }}</div>
          <div class="stat-label">Livraisons en cours</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-clock" style="color: #ed8936;"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ livraisons.retard }}</div>
          <div class="stat-label">En retard</div>
        </div>
      </div>
    </div>

    <!-- Liste des transporteurs -->
    <div class="transporteurs-list">
      <div class="list-header">
        <h2>Liste des transporteurs</h2>
        <div class="search-bar">
          <i class="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Rechercher un transporteur..."
            v-model="searchQuery"
          >
        </div>
      </div>

      <div class="transporteurs-grid">
        <div 
          v-for="transporteur in filteredTransporteurs" 
          :key="transporteur.id"
          class="transporteur-card"
        >
          <div class="transporteur-header">
            <div class="transporteur-logo">
              <img v-if="transporteur.logo" :src="transporteur.logo" :alt="transporteur.nom">
              <i v-else class="fas fa-truck"></i>
            </div>
            <div class="transporteur-info">
              <h3>{{ transporteur.nom }}</h3>
              <p class="transporteur-type">{{ transporteur.type }}</p>
            </div>
            <div class="transporteur-status">
              <span class="status-badge" :class="transporteur.statut">
                {{ transporteur.statut === 'actif' ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
          
          <div class="transporteur-details">
            <div class="detail-item">
              <i class="fas fa-phone"></i>
              <span>{{ transporteur.telephone }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-envelope"></i>
              <span>{{ transporteur.email }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-map-marker-alt"></i>
              <span>{{ transporteur.ville }}</span>
            </div>
          </div>
          
          <div class="transporteur-stats">
            <div class="stat-item">
              <span class="stat-number">{{ transporteur.livraisons }}</span>
              <span class="stat-text">Livraisons</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ transporteur.note }}/5</span>
              <span class="stat-text">Note</span>
            </div>
          </div>
          
          <div class="transporteur-actions">
            <button class="btn btn-sm btn-outline" @click="editTransporteur(transporteur)">
              <i class="fas fa-edit"></i>
              Modifier
            </button>
            <button class="btn btn-sm btn-primary" @click="viewDetails(transporteur)">
              <i class="fas fa-eye"></i>
              Détails
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Ajout/Modification -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fas fa-truck"></i>
            {{ showAddModal ? 'Nouveau transporteur' : 'Modifier le transporteur' }}
          </h3>
          <button class="modal-close" @click="closeModals">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveTransporteur">
            <div class="form-grid">
              <div class="form-group">
                <label>Nom du transporteur</label>
                <input type="text" v-model="currentTransporteur.nom" required>
              </div>
              <div class="form-group">
                <label>Type</label>
                <select v-model="currentTransporteur.type" required>
                  <option value="express">Express</option>
                  <option value="standard">Standard</option>
                  <option value="international">International</option>
                </select>
              </div>
              <div class="form-group">
                <label>Téléphone</label>
                <input type="tel" v-model="currentTransporteur.telephone" required>
              </div>
              <div class="form-group">
                <label>Email</label>
                <input type="email" v-model="currentTransporteur.email" required>
              </div>
              <div class="form-group">
                <label>Ville</label>
                <input type="text" v-model="currentTransporteur.ville" required>
              </div>
              <div class="form-group">
                <label>Statut</label>
                <select v-model="currentTransporteur.statut" required>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline" @click="closeModals">Annuler</button>
          <button class="btn btn-primary" @click="saveTransporteur">
            <i class="fas fa-save"></i>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'Transporteurs',
  setup() {
    const searchQuery = ref('')
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const currentTransporteur = ref({})
    
    const transporteurs = ref([
      {
        id: 1,
        nom: 'DHL Express',
        type: 'express',
        telephone: '+33 1 23 45 67 89',
        email: 'contact@dhl.fr',
        ville: 'Paris',
        statut: 'actif',
        livraisons: 1250,
        note: 4.5,
        logo: null
      },
      {
        id: 2,
        nom: 'Chronopost',
        type: 'express',
        telephone: '+33 1 98 76 54 32',
        email: 'info@chronopost.fr',
        ville: 'Lyon',
        statut: 'actif',
        livraisons: 980,
        note: 4.2,
        logo: null
      },
      {
        id: 3,
        nom: 'Colissimo',
        type: 'standard',
        telephone: '+33 1 11 22 33 44',
        email: 'service@colissimo.fr',
        ville: 'Marseille',
        statut: 'actif',
        livraisons: 2100,
        note: 3.8,
        logo: null
      }
    ])
    
    const livraisons = ref({
      enCours: 45,
      retard: 3
    })
    
    const filteredTransporteurs = computed(() => {
      if (!searchQuery.value) return transporteurs.value
      return transporteurs.value.filter(t => 
        t.nom.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        t.ville.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    })
    
    const editTransporteur = (transporteur) => {
      currentTransporteur.value = { ...transporteur }
      showEditModal.value = true
    }
    
    const viewDetails = (transporteur) => {
      // Logique pour afficher les détails
      console.log('Voir détails:', transporteur)
    }
    
    const saveTransporteur = () => {
      if (showAddModal.value) {
        const newId = Math.max(...transporteurs.value.map(t => t.id)) + 1
        transporteurs.value.push({ ...currentTransporteur.value, id: newId })
      } else {
        const index = transporteurs.value.findIndex(t => t.id === currentTransporteur.value.id)
        if (index !== -1) {
          transporteurs.value[index] = { ...currentTransporteur.value }
        }
      }
      closeModals()
    }
    
    const closeModals = () => {
      showAddModal.value = false
      showEditModal.value = false
      currentTransporteur.value = {}
    }
    
    onMounted(() => {
      // Initialisation
    })
    
    return {
      searchQuery,
      showAddModal,
      showEditModal,
      currentTransporteur,
      transporteurs,
      livraisons,
      filteredTransporteurs,
      editTransporteur,
      viewDetails,
      saveTransporteur,
      closeModals
    }
  }
}
</script>

<style scoped>
.transporteurs {
  padding: 2rem;
  background-color: #f8fafc;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-title i {
  margin-right: 0.75rem;
  color: #667eea;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(102, 126, 234, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
}

.stat-label {
  color: #718096;
  font-size: 0.875rem;
}

.transporteurs-list {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.list-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.search-bar {
  position: relative;
  width: 300px;
}

.search-bar i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #718096;
}

.search-bar input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
}

.transporteurs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.transporteur-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;
}

.transporteur-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.transporteur-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.transporteur-logo {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.transporteur-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.transporteur-logo i {
  font-size: 1.5rem;
  color: #667eea;
}

.transporteur-info {
  flex: 1;
}

.transporteur-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.transporteur-type {
  color: #718096;
  font-size: 0.875rem;
  margin: 0;
  text-transform: capitalize;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.actif {
  background: rgba(72, 187, 120, 0.1);
  color: #48bb78;
}

.status-badge.inactif {
  background: rgba(245, 101, 101, 0.1);
  color: #f56565;
}

.transporteur-details {
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.detail-item i {
  width: 16px;
  margin-right: 0.75rem;
  color: #718096;
}

.transporteur-stats {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
}

.stat-text {
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
}

.transporteur-actions {
  display: flex;
  gap: 0.5rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
}

.modal-header h3 i {
  margin-right: 0.75rem;
  color: #667eea;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
}

.modal-close:hover {
  background: #f7fafc;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

@media (max-width: 768px) {
  .transporteurs {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .list-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .search-bar {
    width: 100%;
  }
  
  .transporteurs-grid {
    grid-template-columns: 1fr;
  }
  
  .transporteur-stats {
    gap: 1rem;
  }

  .stat-item {
    flex: 1;
  }

  .transporteur-actions .btn {
    flex: 1;
    justify-content: center;
  }

  /* Full-screen modal on phones */
  .modal-overlay {
    align-items: stretch;
  }

  .modal-content {
    width: 100%;
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header,
  .modal-footer {
    flex-shrink: 0;
    padding: 1rem 1.25rem;
  }

  .modal-header h3 {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    -webkit-overflow-scrolling: touch;
  }

  .modal-footer .btn {
    flex: 1;
    justify-content: center;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
}

/* ---- Responsive refinements ---- */
/* Header card is white: keep the intended dark title (global .page-header .page-title is white) */
.page-header .page-title {
  color: #2d3748;
  text-shadow: none;
  min-width: 0;
  overflow-wrap: anywhere;
}

.transporteur-info,
.detail-item span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.transporteur-status {
  flex-shrink: 0;
  margin-left: 0.5rem;
}

@media (max-width: 991.98px) {
  .transporteurs-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  }
}

@media (max-width: 575.98px) {
  .page-header {
    padding: 1.25rem;
    align-items: stretch;
  }

  .page-header .btn {
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .stat-card {
    padding: 1rem;
    gap: 0.75rem;
    min-width: 0;
  }

  .stat-icon {
    width: 44px;
    height: 44px;
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .transporteurs-list,
  .transporteur-card {
    padding: 1.25rem;
  }
}

@media (pointer: coarse) {
  .modal-close {
    min-width: 44px;
    min-height: 44px;
  }

  .transporteur-actions .btn {
    min-height: 44px;
  }
}
</style>